import { useTuck } from '../ui/useTuck';
import { useEffect, useRef, useState } from 'preact/hooks';
import { goBack, navigate, type RouteState } from '../lib/router';
import { useFlag } from '../shell/hooks';
import { trailSection } from '../shell/sections';
import { PhoneSearch } from '../shell/Bars';
import { BookView } from '../book/BookView';
import { BackLink } from '../ui/BackLink';
import { EditBar } from '../book/edit';
import { useBookEdits, type BookEdits } from '../book/actions';
import { BookNotFound, loadBook } from '../book/load';
import { bookLabel, labelFromId, statusName } from '../book/model';
import type { BookData } from '../book/types';

// The book page (decisions doc › Book page; boards 2c, 4, 5). Its own address. Laptop: a wide
// side panel over the section it was opened from, with Open full page; opened directly, a full
// page. Phone: a full page with the section bar. Every way back says where it goes.
// Phase 6.1 Part 1: the page shows your book behind the book_page flag; Part 2: moves, marks and
// the fields set in place, each with Undo.

type Loaded =
  | { kind: 'loading' }
  | { kind: 'off' }
  | { kind: 'missing'; label: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; book: BookData };

/** The book, and what changes it: `show` puts a changed copy on the page; `edits` (Part 2) make the changes. */
function useBook(id: string): { loaded: Loaded; edits: BookEdits } {
  const on = useFlag('book_page');
  const [state, setState] = useState<Loaded>({ kind: 'loading' });
  const label = labelFromId(id);
  const edits = useBookEdits(
    state.kind === 'ready' ? state.book : null,
    (book) => setState({ kind: 'ready', book }),
    () => (label === null ? Promise.resolve(null) : loadBook(label)),
  );
  useEffect(() => {
    if (on === null) return;
    if (!on) {
      setState({ kind: 'off' });
      return;
    }
    if (label === null) {
      setState({ kind: 'missing', label: id });
      return;
    }
    let live = true;
    setState({ kind: 'loading' });
    loadBook(label)
      .then((book) => live && setState({ kind: 'ready', book }))
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof BookNotFound) setState({ kind: 'missing', label: bookLabel(label) });
        else setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      live = false;
    };
  }, [id, on]);
  return { loaded: state, edits };
}

const titleOf = (s: Loaded, id: string) => (s.kind === 'ready' ? s.book.work.title : id);

/** The crumb names the book's shelf, as on board 4 ("Currently Reading › The Redbreast"; Kristi, Oct 26);
 *  ← Back still names the section it returns to. Before the book loads, the section. */
const crumbOf = (s: Loaded, id: string, section: string) =>
  `${s.kind === 'ready' ? statusName(s.book.status) : section} › ${titleOf(s, id)}`;

function BookBody({ id, loaded, edits, editing, onRemoved }: { id: string; loaded: Loaded; edits: BookEdits; editing?: boolean; onRemoved?: () => void }) {
  switch (loaded.kind) {
    case 'ready':
      return <BookView book={loaded.book} edits={edits} editing={editing} onRemoved={onRemoved} />;
    case 'loading':
      return (
        <div class="bk-status" aria-busy="true">
          <p class="bk-quiet">Loading {id}…</p>
        </div>
      );
    case 'off':
      return (
        <div class="bk-status">
          <h2 class="book-title">{id}</h2>
          <div class="heads-up">This book page isn’t available.</div>
        </div>
      );
    case 'missing':
      return (
        <div class="bk-status">
          <h2 class="book-title">{loaded.label}</h2>
          <div class="heads-up">There’s no book {loaded.label} on your list.</div>
        </div>
      );
    case 'error':
      return (
        <div class="bk-status">
          <h2 class="book-title">{id}</h2>
          <div class="heads-up">The book couldn’t be loaded: {loaded.message}</div>
        </div>
      );
  }
}

/** The pinned bar's height, for what pins just under it (Expand all / Collapse all; Kristi, Oct 29). */
function useBarHeight() {
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bar.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--book-bar-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  });
  return bar;
}

/** Edit details: edit mode (Part 5) when book_page.edit is on for you; hidden while it's off (R11). */
function useEditDetails(): { on: boolean; editing: boolean; start: () => void; done: () => void } {
  const on = Boolean(useFlag('book_page.edit'));
  const [editing, setEditing] = useState(false);
  return {
    on,
    editing: on && editing,
    start: () => setEditing(true),
    done: () => setEditing(false),
  };
}

/** Laptop, opened from a section: the side panel. ✕ (or Escape, or a click beside it) closes it. */
export function BookPanel({ id, state }: { id: string; state: RouteState }) {
  const from = trailSection(state);
  const bar = useBarHeight();
  const panel = useRef<HTMLElement>(null);
  const { loaded, edits } = useBook(id);
  const edit = useEditDetails();
  const close = () => goBack(from.path);

  useEffect(() => {
    panel.current?.focus();
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, []);

  return (
    <>
      <button type="button" class="panel-scrim" aria-label="Close the book" tabIndex={-1} onClick={close} />
      <aside class="book-panel" aria-label="Book" ref={panel} tabIndex={-1}>
        {/* The panel's pinned bar holds Edit details again (Kristi, Oct 29), with Full page as a pill beside it (Kristi, Oct 29: the ⤢ was hard to read);
            while editing, the bar itself turns teal. */}
        <div class={`book-panel-bar${edit.editing ? ' editing' : ''}`} ref={bar}>
          {edit.editing ? (
            <EditBar title={titleOf(loaded, id)} onDone={edit.done} />
          ) : (
            <>
              <span class="crumb">{crumbOf(loaded, id, from.name)}</span>
              {loaded.kind === 'ready' && edit.on ? (
                <button type="button" class="btn-secondary book-edit" onClick={edit.start}>
                  Edit details
                </button>
              ) : null}
              <button
                type="button"
                class="btn-secondary book-full"
                title="Open as a full page"
                onClick={() => navigate(`/book/${id}`, { replace: true, state: { from: state.from, full: true } })}
              >
                Full page
              </button>
            </>
          )}
          <button type="button" class="round-btn" aria-label="Close" onClick={close}>
            ✕
          </button>
        </div>
        <div class="book-body">
          <BookBody id={id} loaded={loaded} edits={edits} editing={edit.editing} onRemoved={close} />
        </div>
      </aside>
    </>
  );
}

/** The full page: laptop (opened directly or with Open full page) and always on a phone. */
export function BookPage({ id, state, wide }: { id: string; state: RouteState; wide: boolean }) {
  const from = trailSection(state);
  const bar = useBarHeight();
  const edit = useEditDetails();
  const { loaded, edits } = useBook(id);
  // From an author's or a series' page, ← Back names it ("← Back to Jo Nesbø").
  const back = <BackLink where={state.fromName ?? from.name} onClick={() => goBack(from.path)} />;
  // On a phone, Expand all slides away scrolling down and back up, like a page's control bar (check-up, Nov 2).
  const tucked = useTuck(wide);

  if (wide) {
    return (
      <div class="page book-page">
        {/* Pinned under the header, with Edit details at the right as on board 4 (Kristi, Oct 29); teal while editing. */}
        <div class={`book-page-bar${edit.editing ? ' editing' : ''}`} ref={bar}>
          {edit.editing ? (
            <EditBar title={titleOf(loaded, id)} onDone={edit.done} />
          ) : (
            <>
              {back}
              <span class="crumb">{crumbOf(loaded, id, from.name)}</span>
              {loaded.kind === 'ready' && edit.on ? (
                <button type="button" class="btn-secondary book-edit" onClick={edit.start}>
                  Edit details
                </button>
              ) : null}
            </>
          )}
        </div>
        <BookBody id={id} loaded={loaded} edits={edits} editing={edit.editing} onRemoved={() => goBack(from.path)} />
      </div>
    );
  }

  // ♥ Favorite left the top bar for the page, beside ↻ ↺ (Kristi, Oct 27: too easy to tap by
  // accident); search took its place.
  return (
    <div class={`book-phone${tucked ? ' tucked' : ''}`}>
      {/* The phone's pinned top bar: ← Back, search and ✎ Edit, no extra row (Kristi, Oct 27 and 29);
          while editing, the bar itself turns teal with Done. */}
      <header class={`ptop book-phone-bar${edit.editing ? ' editing' : ''}`}>
        {edit.editing ? (
          <EditBar title={titleOf(loaded, id)} onDone={edit.done} />
        ) : (
          <>
            {back}
            <PhoneSearch />
            {loaded.kind === 'ready' && edit.on ? (
              <button type="button" class="btn-secondary book-edit" aria-label="Edit details" onClick={edit.start}>
                ✎ Edit
              </button>
            ) : null}
          </>
        )}
      </header>
      <div class="page">
        <BookBody id={id} loaded={loaded} edits={edits} editing={edit.editing} onRemoved={() => goBack(from.path)} />
      </div>
    </div>
  );
}
